import { useEffect, useRef, useState } from "react";
import { useFile } from "@/hooks/use-files";
import type { PersonalOfficeDoorStatus } from "@/lib/enums";
import type { AvatarLook } from "@/lib/office/avatar-look";
import {
  depthKey,
  drawIsoBox,
  drawIsoFloor,
  drawIsoWalls,
  iso,
  shade,
} from "@/lib/office/iso-draw";
import {
  DEFAULT_OFFICE_THEME,
  normalizeOfficeTheme,
  type OfficeTheme,
} from "@/lib/office/office-theme";
import {
  ensureSprite,
  footprintCenter,
  measureContentHeight,
  resolveFurnitureSprite,
  spriteDrawRect,
  spriteScaleFor,
  warmSprites,
} from "@/lib/office/sprites";
import { unlockSwatch } from "@/lib/office/unlocks";

export type OfficePreviewAvatarLook = Pick<
  AvatarLook,
  "shirt" | "skin" | "pants" | "shoes" | "hair" | "style"
>;

export type OfficePreviewProps = {
  displayName: string;
  doorStatus: PersonalOfficeDoorStatus;
  decorIds: string[];
  wallArtFileId: string | null;
  wallArtPlaceholder: string | null;
  theme: OfficeTheme;
  className?: string;
  /** Canvas CSS width */
  width?: number;
  height?: number;
  /** Optional shirt/skin for a tiny seated avatar at the desk. */
  avatarShirt?: string;
  avatarSkin?: string;
  /** Fuller look for the seated figure (preferred over shirt/skin alone). */
  avatarLook?: OfficePreviewAvatarLook;
  /** Skip canvas backdrop when parent already paints the stage frame. */
  transparentBg?: boolean;
};

/** World engine tile width; preview scales sprites by tw/ENGINE_TW. */
const ENGINE_TW = 64;
/** Pod footprint in grid units. */
const POD = 4;

type LayerItem = {
  depth: number;
  draw: () => void;
};

/**
 * Lightweight isometric pod preview for the dashboard ready room.
 * Not the full world engine — enough to show plaque, door, theme, furniture, wall photo.
 * Uses hero sprites when real art exists; otherwise procedural iso furniture.
 */
export function OfficePreview({
  displayName,
  doorStatus,
  decorIds,
  wallArtFileId,
  wallArtPlaceholder,
  theme: themeIn,
  className,
  width = 280,
  height = 200,
  avatarShirt = "#1E6CC7",
  avatarSkin = "#E9BE93",
  avatarLook,
  transparentBg = false,
}: OfficePreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { data: wallFile } = useFile(wallArtFileId);
  const theme = normalizeOfficeTheme(themeIn ?? DEFAULT_OFFICE_THEME);
  const photoUrl = wallFile?.thumbnailUrl ?? wallFile?.url ?? wallArtPlaceholder;
  const [photoEl, setPhotoEl] = useState<HTMLImageElement | null>(null);
  /** Bumps redraw when catalog sprites finish loading. */
  const [spriteEpoch, setSpriteEpoch] = useState(0);

  const shirt = avatarLook?.shirt ?? avatarShirt;
  const skin = avatarLook?.skin ?? avatarSkin;
  const pants = avatarLook?.pants ?? "#1F2A44";
  const shoes = avatarLook?.shoes ?? "#001847";
  const hair = avatarLook?.hair ?? "#2B2118";

  useEffect(() => {
    if (!photoUrl) {
      setPhotoEl(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setPhotoEl(img);
    img.onerror = () => setPhotoEl(null);
    img.src = photoUrl;
  }, [photoUrl]);

  // Warm desk / chair / hero decor sprites used by this preview.
  useEffect(() => {
    const kinds = [
      { kind: "desk", dir: "N" },
      { kind: "chair", dir: "S" },
      ...decorIds.slice(0, 4).map((kind) => ({ kind, dir: "S" as const })),
    ];
    const srcs: string[] = [];
    for (const k of kinds) {
      const r = resolveFurnitureSprite(k.kind, k.dir);
      // Catalog is the source of truth for real art vs procedural placeholders
      if (r.useSprite && r.asset) srcs.push(r.asset.src);
    }
    warmSprites(srcs);
    // Poll briefly until ready (or failed) so the canvas upgrades from procedural.
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      setSpriteEpoch((t) => t + 1);
      const allSettled = srcs.every((s) => ensureSprite(s) !== null);
      // also stop if still missing after ~2s (failed loads stay null forever)
      if (allSettled || n > 20) window.clearInterval(id);
    }, 100);
    return () => window.clearInterval(id);
  }, [decorIds]);

  // spriteEpoch intentionally forces repaint when assets load (not a "data" dependency of the draw).
  useEffect(() => {
    void spriteEpoch;
    const cv = canvasRef.current;
    if (!cv) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = width * dpr;
    cv.height = height * dpr;
    cv.style.width = `${width}px`;
    cv.style.height = `${height}px`;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, width, height);

    // Soft stage backdrop — skip when parent already frames the stage
    if (!transparentBg) {
      const bg = ctx.createLinearGradient(0, 0, 0, height);
      bg.addColorStop(0, "#152848");
      bg.addColorStop(0.55, "#0E1F40");
      bg.addColorStop(1, "#0A1834");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);
      const glow = ctx.createRadialGradient(
        width / 2,
        height * 0.72,
        8,
        width / 2,
        height * 0.72,
        Math.min(width, height) * 0.45,
      );
      glow.addColorStop(0, "rgba(45,155,214,0.2)");
      glow.addColorStop(1, "rgba(45,155,214,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, width, height);
    }

    // Scale tile size to fill the canvas with a 4×4 pod + wall height.
    const tw = Math.max(36, Math.min(52, Math.floor(width / 7.2)));
    const th = tw / 2;
    const wallH = Math.round(tw * 1.15);
    const ox = width / 2;
    const oy = height * 0.58;

    const toIso = (gx: number, gy: number) => iso(ox, oy, tw, th, gx, gy);
    const scale = tw / ENGINE_TW;

    const trySprite = (
      kind: string,
      dir: string,
      gx: number,
      gy: number,
      fw = 1,
      fd = 1,
    ): boolean => {
      const resolved = resolveFurnitureSprite(kind, dir);
      if (!resolved.asset || !resolved.useSprite) return false;
      const img = ensureSprite(resolved.asset.src);
      if (!img) return false;
      const { cx, cy } = footprintCenter(gx, gy, fw, fd);
      const center = toIso(cx, cy);
      // Same person-anchored relative scale as the world engine so previews match.
      const contentH = measureContentHeight(img) || resolved.asset.height;
      const rect = spriteDrawRect(
        center.x,
        center.y,
        resolved.asset,
        scale * spriteScaleFor(kind, contentH),
      );
      try {
        ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
        return true;
      } catch {
        return false;
      }
    };

    const boxAt = (
      gx: number,
      gy: number,
      w: number,
      d: number,
      h: number,
      top: string,
      faceOpts?: { left?: string; right?: string },
    ) => drawIsoBox(ctx, ox, oy, tw, th, gx, gy, w, d, h, top, faceOpts);

    // --- Floor + walls (static layers) ---
    drawIsoFloor(ctx, ox, oy, tw, th, POD, theme.rugColor, { checker: true });
    drawIsoWalls(ctx, ox, oy, tw, th, POD, theme.wallColor, { wallH });

    // Wall photo on N wall face (back wall, above furniture)
    {
      const mid = toIso(POD / 2, 0.12);
      const frameW = Math.round(tw * 1.05);
      const frameH = Math.round(wallH * 0.55);
      const frameX = mid.x - frameW / 2;
      const frameY = mid.y - wallH + 8;
      // Soft wall-plane tilt: draw mat then image; slight offset for depth
      ctx.fillStyle = "#3D2914";
      ctx.fillRect(frameX - 3, frameY - 3, frameW + 6, frameH + 6);
      ctx.fillStyle = shade("#3D2914", 25);
      ctx.fillRect(frameX - 1, frameY - 1, frameW + 2, frameH + 2);
      ctx.fillStyle = "#F5E6D3";
      ctx.fillRect(frameX, frameY, frameW, frameH);
      if (photoEl?.complete && photoEl.naturalWidth > 0) {
        try {
          ctx.drawImage(photoEl, frameX + 3, frameY + 3, frameW - 6, frameH - 6);
        } catch {
          ctx.fillStyle = "rgba(0,24,71,0.25)";
          ctx.fillRect(frameX + 3, frameY + 3, frameW - 6, frameH - 6);
        }
      } else {
        ctx.fillStyle = "rgba(0,24,71,0.28)";
        ctx.fillRect(frameX + 3, frameY + 3, frameW - 6, frameH - 6);
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.font = `bold ${Math.max(9, Math.round(tw * 0.18))}px sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(photoUrl ? "…" : "PHOTO", frameX + frameW / 2, frameY + frameH / 2 + 3);
        ctx.textAlign = "start";
      }
    }

    const layers: LayerItem[] = [];

    // Desk
    layers.push({
      depth: depthKey(2.4, 1.55, 0),
      draw: () => {
        if (trySprite("desk", "N", 2.1, 1.3, 1.2, 0.9)) return;
        boxAt(2.05, 1.35, 1.3, 0.85, 14, "#5C4033", {
          left: "#3D2914",
          right: "#4A3428",
        });
        // Monitor
        boxAt(2.4, 1.45, 0.55, 0.2, 12, "#1F2A44", {
          left: "#101728",
          right: "#2A3654",
        });
        boxAt(2.45, 1.48, 0.45, 0.12, 8, "#2D9BD6");
      },
    });

    // Chair
    layers.push({
      depth: depthKey(1.45, 1.55, 0),
      draw: () => {
        if (trySprite("chair", "S", 1.2, 1.35, 0.9, 0.9)) return;
        boxAt(1.25, 1.4, 0.7, 0.65, 8, "#1F2A44", {
          left: "#101728",
          right: "#2A3654",
        });
        boxAt(1.3, 1.35, 0.6, 0.25, 16, "#1E6CC7", {
          left: shade("#1E6CC7", -25),
        });
      },
    });

    // Seated avatar
    layers.push({
      depth: depthKey(1.55, 1.55, 0.4),
      draw: () => {
        drawSeatedAvatar(ctx, toIso(1.55, 1.55), {
          shirt,
          skin,
          pants,
          shoes,
          hair,
          u: Math.max(1.6, scale * 2.1),
        });
      },
    });

    // Floor decor (up to 4)
    const decorSlots: Array<[number, number]> = [
      [2.85, 2.7],
      [1.05, 2.7],
      [3.0, 1.15],
      [0.9, 1.15],
    ];
    decorIds.slice(0, 4).forEach((decorId, i) => {
      const slot = decorSlots[i] ?? decorSlots[0];
      if (!slot) return;
      const [ogx, ogy] = slot;
      layers.push({
        depth: depthKey(ogx, ogy, 0.1),
        draw: () => {
          if (trySprite(decorId, "S", ogx, ogy)) return;
          drawProceduralDecor(ctx, ox, oy, tw, th, ogx, ogy, decorId);
        },
      });
    });

    // Door on south edge
    layers.push({
      depth: depthKey(2, POD, 1),
      draw: () => {
        drawDoor(ctx, ox, oy, tw, th, doorStatus === "open", displayName);
      },
    });

    layers.sort((a, b) => a.depth - b.depth);
    for (const layer of layers) layer.draw();
  }, [
    displayName,
    doorStatus,
    decorIds,
    photoEl,
    photoUrl,
    theme,
    width,
    height,
    spriteEpoch,
    shirt,
    skin,
    pants,
    shoes,
    hair,
    transparentBg,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      width={width}
      height={height}
      aria-label={`Preview of ${displayName || "office"}`}
    />
  );
}

function drawSeatedAvatar(
  ctx: CanvasRenderingContext2D,
  seat: { x: number; y: number },
  look: {
    shirt: string;
    skin: string;
    pants: string;
    shoes: string;
    hair: string;
    u: number;
  },
): void {
  const { shirt, skin, pants, shoes, hair, u } = look;
  const ax = seat.x;
  const ay = seat.y - 6;
  const outline = "#101728";

  const r = (x: number, y: number, w: number, h: number, c: string) => {
    ctx.fillStyle = c;
    ctx.fillRect(
      Math.round(ax + x * u),
      Math.round(ay + y * u),
      Math.max(1, Math.round(w * u)),
      Math.max(1, Math.round(h * u)),
    );
  };
  const ro = (x: number, y: number, w: number, h: number, c: string) => {
    r(x - 0.4, y - 0.4, w + 0.8, h + 0.8, outline);
    r(x, y, w, h, c);
  };

  // Shoes / legs (seated)
  ro(-3.2, -1, 2.8, 2.2, shoes);
  ro(0.6, -1, 2.8, 2.2, shoes);
  ro(-3, -4.5, 2.6, 3.5, pants);
  ro(0.5, -4.5, 2.6, 3.5, pants);
  // Torso
  ro(-3.5, -11, 7.2, 7, shirt);
  r(2.5, -10.5, 1.2, 5.5, shade(shirt, -28));
  // Head
  ro(-3, -17.5, 6.2, 6.5, skin);
  r(-2.2, -16.8, 2.2, 1.2, shade(skin, 18));
  // Eyes
  r(-1.6, -14.8, 1.4, 1.4, "#FFFFFF");
  r(0.8, -14.8, 1.4, 1.4, "#FFFFFF");
  r(-1.1, -14.8, 0.7, 1.2, outline);
  r(1.3, -14.8, 0.7, 1.2, outline);
  // Hair cap
  ro(-3.2, -19.2, 6.6, 2.4, hair);
  r(-3.2, -18.2, 1.4, 2.5, hair);
  r(2.2, -18.2, 1.4, 2.5, hair);
  // Arms on desk
  ro(-5.2, -9.5, 2, 4, shirt);
  ro(3.5, -9.5, 2, 4, shirt);
  ro(-5.4, -6.2, 2.2, 1.6, skin);
  ro(3.6, -6.2, 2.2, 1.6, skin);
}

function drawDoor(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  tw: number,
  th: number,
  open: boolean,
  displayName: string,
): void {
  const doorGx = 1.55;
  const doorGy = POD - 0.35;
  const frame = "#3D2914";
  // Frame plinth
  drawIsoBox(ctx, ox, oy, tw, th, doorGx, doorGy, 0.95, 0.28, 4, frame, {
    left: shade(frame, -20),
    right: shade(frame, -10),
  });
  if (open) {
    // Dark doorway
    drawIsoBox(ctx, ox, oy, tw, th, doorGx + 0.08, doorGy + 0.02, 0.55, 0.2, 30, "#06122E", {
      left: "#040a18",
      right: "#0a1830",
    });
    // Open leaf swung right
    drawIsoBox(ctx, ox, oy, tw, th, doorGx + 0.55, doorGy - 0.05, 0.32, 0.35, 28, "#2EA86A", {
      left: shade("#2EA86A", -30),
      right: shade("#2EA86A", -12),
    });
  } else {
    drawIsoBox(ctx, ox, oy, tw, th, doorGx + 0.08, doorGy + 0.02, 0.78, 0.22, 30, "#C43B35", {
      left: shade("#C43B35", -28),
      right: shade("#C43B35", -12),
    });
    // Handle
    const h = iso(ox, oy, tw, th, doorGx + 0.7, doorGy + 0.12);
    ctx.fillStyle = "#E8A93C";
    ctx.fillRect(h.x - 1, h.y - 14, 3, 3);
  }

  const plaque = iso(ox, oy, tw, th, doorGx + 0.48, doorGy + 0.05);
  const label = (displayName || "Office").slice(0, 14);
  ctx.font = `bold ${Math.max(9, Math.round(tw * 0.2))}px Montserrat, sans-serif`;
  ctx.textAlign = "center";
  const pw = Math.max(72, label.length * 7 + 16);
  ctx.fillStyle = "rgba(0,24,71,0.88)";
  ctx.fillRect(plaque.x - pw / 2, plaque.y - 48, pw, 15);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(label, plaque.x, plaque.y - 37);
  ctx.font = `bold ${Math.max(8, Math.round(tw * 0.16))}px Raleway, sans-serif`;
  ctx.fillStyle = open ? "#2EA86A" : "#A8B4C8";
  ctx.fillText(open ? "OPEN" : "CLOSED", plaque.x, plaque.y - 8);
  ctx.textAlign = "start";
}

function drawProceduralDecor(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  tw: number,
  th: number,
  gx: number,
  gy: number,
  decorId: string,
): void {
  const sw = unlockSwatch(decorId);
  const box = (
    bx: number,
    by: number,
    w: number,
    d: number,
    h: number,
    top: string,
    face?: { left?: string; right?: string },
  ) => drawIsoBox(ctx, ox, oy, tw, th, bx, by, w, d, h, top, face);

  switch (decorId) {
    case "plant":
    case "cactus": {
      box(gx + 0.25, gy + 0.25, 0.5, 0.5, 6, "#6B5B4F");
      box(gx + 0.32, gy + 0.32, 0.36, 0.36, 22, sw, { left: shade(sw, -25) });
      if (decorId === "cactus") {
        box(gx + 0.15, gy + 0.38, 0.18, 0.18, 10, sw);
        box(gx + 0.68, gy + 0.38, 0.16, 0.16, 8, sw);
      } else {
        box(gx + 0.18, gy + 0.35, 0.22, 0.22, 8, shade(sw, 15));
        box(gx + 0.58, gy + 0.32, 0.22, 0.22, 10, shade(sw, -10));
      }
      break;
    }
    case "lamp": {
      box(gx + 0.35, gy + 0.35, 0.3, 0.3, 6, "#8894A8");
      box(gx + 0.42, gy + 0.42, 0.16, 0.16, 18, "#5C5C5C");
      box(gx + 0.28, gy + 0.28, 0.44, 0.44, 12, sw, { left: shade(sw, -25) });
      break;
    }
    case "fox":
    case "statue":
    case "trophy": {
      box(gx + 0.2, gy + 0.2, 0.6, 0.6, 5, "#8894A8");
      box(gx + 0.28, gy + 0.28, 0.44, 0.44, 18, sw, { left: shade(sw, -20) });
      box(gx + 0.34, gy + 0.34, 0.32, 0.32, 8, shade(sw, 20));
      break;
    }
    case "arcade":
    case "jukebox": {
      box(gx + 0.18, gy + 0.18, 0.64, 0.64, 28, sw, {
        left: shade(sw, -25),
        right: shade(sw, -10),
      });
      box(gx + 0.28, gy + 0.28, 0.44, 0.28, 10, "#06122E");
      box(gx + 0.32, gy + 0.32, 0.36, 0.18, 6, "#2D9BD6");
      break;
    }
    case "bookshelf": {
      box(gx + 0.12, gy + 0.15, 0.76, 0.7, 32, "#C9D0DC", {
        left: "#8894A8",
        right: shade("#C9D0DC", -12),
      });
      box(gx + 0.18, gy + 0.22, 0.64, 0.12, 6, "#1E6CC7");
      box(gx + 0.18, gy + 0.4, 0.64, 0.12, 6, "#64479D");
      box(gx + 0.18, gy + 0.58, 0.64, 0.12, 6, "#E8A93C");
      break;
    }
    case "sign":
    case "neon": {
      box(gx + 0.1, gy + 0.35, 0.8, 0.25, 18, "#001847", {
        left: "#000d28",
        right: "#0a2048",
      });
      box(gx + 0.18, gy + 0.4, 0.64, 0.12, 8, sw);
      break;
    }
    case "coffee": {
      box(gx + 0.15, gy + 0.2, 0.7, 0.55, 10, "#5C4033");
      box(gx + 0.25, gy + 0.28, 0.5, 0.35, 14, sw, { left: shade(sw, -20) });
      box(gx + 0.35, gy + 0.35, 0.3, 0.2, 6, "#F5E6D3");
      break;
    }
    case "guitar": {
      box(gx + 0.35, gy + 0.2, 0.3, 0.55, 4, "#3D2914");
      box(gx + 0.28, gy + 0.45, 0.44, 0.35, 10, sw, { left: shade(sw, -20) });
      break;
    }
    case "fishbowl": {
      box(gx + 0.28, gy + 0.28, 0.44, 0.44, 4, "#C9D0DC");
      box(gx + 0.3, gy + 0.3, 0.4, 0.4, 14, "#7EC8E8", {
        left: shade("#7EC8E8", -20),
      });
      break;
    }
    default: {
      box(gx + 0.2, gy + 0.2, 0.6, 0.6, 14, sw, {
        left: shade(sw, -25),
        right: shade(sw, -12),
      });
      break;
    }
  }
}
